






























































































































import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import isEqual from 'lodash/isEqual';
import * as vr from '@/helpers/validation';
import {
  integer,
  required,
  validNumber,
  positiveNumber
} from '@/helpers/validation';

import {
  updateCopystakeIntegration,
  updateCopystakeIntegrationMode,
  updateCopystakeGameIntegrationType
} from '@/api/CopyStake/Integration';
import { AxiosError } from 'axios';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import {
  CopyStakeCopyModeNaming,
  CopyStakeIntegrationModeNaming,
  ECopyStakeCopyMode,
  ECopyStakeIntegrationMode
} from '@/api/schema';
import { generateNocodeIntegrationCode } from '@/helpers/copyStakeHelpers';

const CodeHighlightedBlock = (): Promise<any> =>
  import('@/components/CodeHighlightedBlock.vue');
const OnboardingCopystakeAggregationSettings = (): Promise<any> =>
  import(
    '@/views/OnboardingView/onboardingStepsComponents/OnboardingCopystakeAggregationSettings.vue'
  );

enum INTEGRATION_TYPE {
  DEFAULT_INTEGRATION = 'DEFAULT_INTEGRATION',
  UNDERGROUND_CASINO = 'UNDERGROUND_CASINO'
}

type TSettingsValues = {
  copyMode: ECopyStakeCopyMode;
  apiKey: string;
  ttl: string | number;
  callbackUrl: string;
  brandName: string;
  gameIntegrationType: INTEGRATION_TYPE | null;
};

type TRadioOption = {
  value: string;
  title: string;
  description: string;
};

type TSettingsData = {
  ECopyStakeIntegrationMode?: typeof ECopyStakeIntegrationMode;
  integrationModesList: ReadonlyArray<{
    text: string;
    value: ECopyStakeIntegrationMode;
  }>;
  copyModesList: ReadonlyArray<{
    text: string;
    value: ECopyStakeCopyMode;
  }>;

  values: TSettingsValues;
  formValid: boolean;
  isSubmitting: boolean;
  isSubmittingIntegrationMode: boolean;
  selectedIntegration: string | null;
  options: TRadioOption[];
};

type TFormField = {
  label: string;
  tooltip: string;
  key: string;
  classes: Record<string, boolean>;
};

type TValidator = (v: string) => boolean | string;

export default {
  name: 'OnboardingCopystakeSettings',
  components: {
    CodeHighlightedBlock,
    OnboardingCopystakeAggregationSettings
  },
  data(): TSettingsData {
    return {
      ECopyStakeIntegrationMode,
      integrationModesList: Object.freeze([
        {
          text: CopyStakeIntegrationModeNaming.GENERAL,
          value: ECopyStakeIntegrationMode.GENERAL
        },
        {
          text: CopyStakeIntegrationModeNaming.NOCODE,
          value: ECopyStakeIntegrationMode.NOCODE
        },
        {
          text: CopyStakeIntegrationModeNaming.AGGREGATION,
          value: ECopyStakeIntegrationMode.AGGREGATION
        }
      ]),
      copyModesList: Object.freeze([
        {
          text: CopyStakeCopyModeNaming.ON_FLY,
          value: ECopyStakeCopyMode.ON_FLY
        },
        {
          text: CopyStakeCopyModeNaming.IN_POOL,
          value: ECopyStakeCopyMode.IN_POOL
        }
      ]),

      values: {
        copyMode: ECopyStakeCopyMode.ON_FLY,
        apiKey: null,
        ttl: null,
        callbackUrl: null,
        brandName: 'Copystake',
        gameIntegrationType: null
      },

      formValid: false,
      isSubmitting: false,
      isSubmittingIntegrationMode: false,
      selectedIntegration: null,
      options: [
        {
          value: INTEGRATION_TYPE.UNDERGROUND_CASINO,
          title: 'Underground Casino',
          description: 'White-label site, integrated as a single game'
        },
        {
          value: INTEGRATION_TYPE.DEFAULT_INTEGRATION,
          title: 'Default Integration',
          description: 'Standard integration for all available games'
        }
      ]
    };
  },
  computed: {
    ...mapState('app', ['isSuperAdmin']),
    ...mapState('Onboarding', [
      'copyStakeIntegration',
      'copyStakeAggregationConfig',
      'loading'
    ]),
    ...mapGetters('SuperAdmin', ['currentOperator']),

    savedValues(): TSettingsValues {
      return {
        copyMode:
          this.copyStakeIntegration.copyMode || ECopyStakeCopyMode.ON_FLY,
        apiKey: this.copyStakeIntegration.apiKey || null,
        ttl: this.copyStakeIntegration.ttl || null,
        callbackUrl: this.copyStakeIntegration.callbackUrl || null,
        brandName: this.copyStakeIntegration.brandName,
        gameIntegrationType: this.copyStakeIntegration.gameIntegrationType
      };
    },
    integrationMode(): ECopyStakeIntegrationMode {
      return (
        this.copyStakeIntegration.integrationMode ||
        ECopyStakeIntegrationMode.GENERAL
      );
    },

    canUpdate(): boolean {
      return this.$role.can.update('copy_stake streamers');
    },
    isLoadingIntegrationMode(): boolean {
      return (
        this.loading.copyStakeIntegration || this.isSubmittingIntegrationMode
      );
    },
    isDisabledIntegrationModeSelect(): boolean {
      return !!(
        this.isReadonly || this.copyStakeAggregationConfig?.operatorType
      );
    },
    isReadonly(): boolean {
      return (
        !this.canUpdate ||
        this.loading.copyStakeIntegration ||
        this.isSubmitting ||
        this.isSubmittingIntegrationMode ||
        this.isLoadingIntegrationMode
      );
    },
    isUpdated(): boolean {
      return !isEqual(this.savedValues, this.values);
    },
    submitDisabled(): boolean {
      return (
        !this.isUpdated ||
        !this.formValid ||
        this.loading.copyStakeIntegration
      );
    },

    validationRules(): {
      apiKey: TValidator[];
      ttl: TValidator[];
      callbackUrl: TValidator[];
      brandName: TValidator[];
    } {
      return {
        apiKey: [(v) => required(v, 'API Key')],
        ttl: [
          (v) => required(v, 'TTL Session'),
          integer,
          validNumber,
          positiveNumber
        ],
        callbackUrl: [(v) => required(v, 'Callback URL'), vr.url],
        brandName: [(v) => required(v, 'App Name')]
      };
    },
    fields(): TFormField[] {
      return [
        {
          label: 'API Key',
          tooltip: 'API Key for integration',
          key: 'apiKey',
          classes: { 'no-pointer-events': !this.canUpdate }
        },
        {
          label: 'TTL Session',
          tooltip: 'TTL for session synchronization in a minutes',
          key: 'ttl',
          classes: { 'no-pointer-events': !this.canUpdate }
        },
        {
          label: 'Callback URL',
          tooltip: 'Callback URL for integration',
          key: 'callbackUrl',
          classes: { 'no-pointer-events': !this.canUpdate }
        }
      ];
    },
    nocodeIntegrationCode(): string {
      return generateNocodeIntegrationCode(
        this.copyStakeIntegration.integrationId
      );
    }
  },
  mounted(): void {
    this.getSavedSettings();
  },
  methods: {
    ...mapActions('Onboarding', ['getCopyStakeIntegrationData']),
    ...mapMutations('Onboarding', ['setCopyStakeIntegrationData']),
    updateSettings(): void {
      if (this.isSubmitting) return;

      this.isSubmitting = true;

      updateCopystakeIntegration(this.values)
        .then(() => {
          this.getSavedSettings();

          this.$toast.success('CopyStake settings updated.');
        })
        .catch(() => this.$toast.error('CopyStake settings update failed.'))
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    getSavedSettings(): void {
      this.getCopyStakeIntegrationData()
        .then(() => {
          this.values.copyMode =
            this.copyStakeIntegration.copyMode || ECopyStakeCopyMode.ON_FLY;
          this.values.apiKey = this.copyStakeIntegration.apiKey || null;
          this.values.ttl = this.copyStakeIntegration.ttl || null;
          this.values.callbackUrl =
            this.copyStakeIntegration.callbackUrl || null;
          this.values.brandName =
            this.copyStakeIntegration.brandName || 'Copystake';
          this.values.gameIntegrationType =
            this.copyStakeIntegration.gameIntegrationType;

          this.selectedIntegration =
            this.copyStakeIntegration.gameIntegrationType;
        })
        .catch((err: AxiosError) => {
          errorToastMessage(err);
        });
    },
    handlerChangeCopyStakeIntegrationMode(
      integrationMode: ECopyStakeIntegrationMode
    ): void {
      this.isSubmittingIntegrationMode = true;

      updateCopystakeIntegrationMode(integrationMode)
        .then(() => {
          this.$toast.success('CopyStake Integration Mode updated.');
        })
        .catch((err: AxiosError) => {
          errorToastMessage(err);
        })
        .finally(() => {
          this.getSavedSettings();

          this.isSubmittingIntegrationMode = false;
        });
    },

    async updateIntegrationType(): Promise<void> {
      await updateCopystakeGameIntegrationType({
        operatorId: this.currentOperator.id,
        gameIntegrationType: this.selectedIntegration
      });

      this.values.gameIntegrationType = this.selectedIntegration;
    }
  }
};
